import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import {
  ArrowLeft,
  Plug,
  Mail,
  Shield,
  RefreshCw,
  Link2,
} from "lucide-react";
import { getFlags } from "../store/thunks/hostActions";
import PmsConnectionPanel from "../components/onboarding/PmsConnectionPanel";
import EntryInstructionEmailPreference from "../components/onboarding/EntryInstructionEmailPreference";

const INTEGRATIONS_TOAST_STORAGE_KEY = "pending_integration_toast";

const IntegrationsScreen = () => {
  const dispatch = useDispatch();
  const { hostInfo } = useSelector((state) => state.host);

  useEffect(() => {
    const pendingToastRaw = sessionStorage.getItem(INTEGRATIONS_TOAST_STORAGE_KEY);
    if (!pendingToastRaw) return;
    sessionStorage.removeItem(INTEGRATIONS_TOAST_STORAGE_KEY);
    try {
      const pendingToast = JSON.parse(pendingToastRaw);
      if (pendingToast?.type === "success") {
        toast.success(pendingToast.message || "Integration updated.");
      } else if (pendingToast?.type === "error") {
        toast.error(pendingToast.message || "Integration update failed.");
      }
    } catch (_) {
      /* ignore malformed session values */
    }
  }, []);

  useEffect(() => {
    if (hostInfo?._id) dispatch(getFlags(hostInfo._id));
  }, [dispatch, hostInfo?._id]);

  return (
    <div
      className="relative w-full min-h-[100dvh] bg-[#FAFAFA]"
      style={{
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <div className="mx-auto max-w-[1200px] px-6 py-8 laptop:py-10">
        {/* Header */}
        <div className="flex items-start justify-between gap-4 flex-wrap mb-6 laptop:mb-8">
          <div>
            <div className="inline-flex items-center gap-2 mb-2">
              <span className="w-1.5 h-1.5 rounded-full bg-[#EF4949]" />
              <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#888888]">
                Settings · Integrations
              </span>
            </div>
            <h1
              className="text-[30px] laptop:text-[36px] font-extrabold text-[#111111] leading-tight"
              style={{ letterSpacing: -0.6 }}
            >
              Connect the systems that run your rentals.
            </h1>
            <p className="mt-1.5 text-[13px] text-[#5F6368] max-w-[620px]">
              Sync properties from your PMS and control how you stay informed
              when guests arrive.
            </p>
          </div>
          <Link
            to="/properties"
            className="inline-flex items-center gap-2 px-4 py-2.5 rounded-[12px] bg-white border border-[#EAEAEA] text-[13px] font-bold text-[#111111] hover:bg-[#F7F7F8] transition-colors"
          >
            <ArrowLeft size={13} strokeWidth={2.5} />
            Back to properties
          </Link>
        </div>

        {/* Two-column composition */}
        <div className="grid grid-cols-1 laptop:grid-cols-[1.3fr_0.7fr] gap-5">
          {/* Left — panels */}
          <div className="space-y-4">
            <SectionCard
              icon={<Plug size={13} className="text-[#EF4949]" />}
              eyebrow="Property Management"
              title="Your PMS connection"
              desc="Import rentals and keep them in sync automatically."
            >
              <PmsConnectionPanel hostInfo={hostInfo} />
            </SectionCard>

            <SectionCard
              icon={<Mail size={13} className="text-[#EF4949]" />}
              eyebrow="Notifications"
              title="Entry instruction emails"
              desc="Get notified when orders need entry instructions or delivery timing context."
            >
              <EntryInstructionEmailPreference
                hostId={hostInfo?._id}
                title={null}
                description={null}
                footerLink={{
                  href: `/settings/${hostInfo?._id}`,
                  label: "Profile Settings",
                }}
              />
            </SectionCard>
          </div>

          {/* Right — "what syncs" context panel */}
          <div className="laptop:sticky laptop:top-8 laptop:self-start">
            <div className="rounded-[20px] bg-[#111111] text-white overflow-hidden">
              <div className="p-6">
                <div className="inline-flex items-center gap-2 mb-3">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#EF4949]" />
                  <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-white/70">
                    What syncs
                  </span>
                </div>
                <h3
                  className="text-[20px] font-extrabold leading-tight"
                  style={{ letterSpacing: -0.4 }}
                >
                  Bellhopt reads the data it needs — nothing more.
                </h3>
                <p className="mt-2 text-[12px] text-white/70 leading-relaxed">
                  Once connected, your properties, bookings, and check-in
                  timings flow in automatically.
                </p>
              </div>

              <div className="border-t border-white/10">
                <SyncRow
                  icon={<Link2 size={14} />}
                  label="Properties & listings"
                  hint="Names, photos, capacity"
                />
                <SyncRow
                  icon={<RefreshCw size={14} />}
                  label="Bookings & calendars"
                  hint="Arrival / departure times"
                />
                <SyncRow
                  icon={<Shield size={14} />}
                  label="Read-only by default"
                  hint="We never modify listings"
                />
              </div>

              <div className="bg-white/5 border-t border-white/10 px-6 py-4">
                <p className="text-[11px] text-white/70">
                  Need another integration?{" "}
                  <Link
                    to="/support"
                    className="font-bold text-white hover:underline"
                  >
                    Talk to us
                  </Link>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
      <ToastContainer />
    </div>
  );
};

function SectionCard({ icon, eyebrow, title, desc, children }) {
  return (
    <div className="rounded-[20px] bg-white border border-[#EAEAEA] overflow-hidden shadow-[0_18px_60px_rgba(0,0,0,0.04)]">
      <div className="px-5 laptop:px-6 pt-5 pb-4 border-b border-[#EAEAEA] bg-gradient-to-b from-white to-[#FAFAFA]">
        <div className="inline-flex items-center gap-1.5 mb-1.5">
          {icon}
          <span className="text-[9px] font-bold uppercase tracking-[0.22em] text-[#888888]">
            {eyebrow}
          </span>
        </div>
        <h2
          className="text-[18px] laptop:text-[20px] font-extrabold text-[#111111] leading-tight"
          style={{ letterSpacing: -0.3 }}
        >
          {title}
        </h2>
        {desc && <p className="mt-1 text-[12px] text-[#5F6368]">{desc}</p>}
      </div>
      <div className="p-5 laptop:p-6">{children}</div>
    </div>
  );
}

function SyncRow({ icon, label, hint }) {
  return (
    <div className="flex items-center gap-3 px-6 py-3 border-b border-white/10 last:border-b-0">
      <span className="w-7 h-7 rounded-[8px] bg-white/10 flex items-center justify-center text-white/80">
        {icon}
      </span>
      <div className="flex-1 min-w-0">
        <div className="text-[12px] font-bold text-white">{label}</div>
        <div className="text-[10px] text-white/60 mt-0.5">{hint}</div>
      </div>
    </div>
  );
}

export default IntegrationsScreen;
